import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
  collection, query, where, onSnapshot, doc, deleteDoc,
} from 'firebase/firestore';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fileIcon(name = '') {
  const ext = name.split('.').pop().toLowerCase();
  if (['doc', 'docx'].includes(ext)) return '📝';
  if (['xls', 'xlsx'].includes(ext)) return '📊';
  if (['ppt', 'pptx'].includes(ext)) return '📑';
  if (ext === 'pdf') return '📄';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return '🖼️';
  return '📎';
}

function formatDate(val) {
  if (!val) return '—';
  try {
    const d = typeof val.toDate === 'function' ? val.toDate() : new Date(val);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

// ── TaskDetail ────────────────────────────────────────────────────────────────

export default function TaskDetail({ task, approvedStudents, onBack, onEdit }) {
  const [submissions, setSubmissions] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!task?.id) return;
    const q = query(
      collection(db, 'taskSubmissions'),
      where('taskId', '==', task.id)
    );
    const unsub = onSnapshot(q, (snap) => {
      setSubmissions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [task?.id]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this task? Student submissions will remain but the task will disappear.')) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'tasks', task.id));
      onBack?.();
    } catch (err) {
      console.error('Delete task error:', err);
      setDeleting(false);
    }
  };

  const submissionMap = {};
  submissions.forEach((s) => { submissionMap[s.studentId] = s; });

  const submittedCount = task.assignedStudentIds?.filter(
    (sid) => submissionMap[sid]
  ).length || 0;

  const totalCount = task.assignedStudentIds?.length || 0;
  const pendingCount = totalCount - submittedCount;
  const isDue = task.dueDate && new Date(task.dueDate) < new Date();

  return (
    <div className="dashboard-wrapper">
      <header className="dashboard-header">
        <div className="header-left">
          <span className="header-logo">🪺 LittleNest</span>
          <div className="header-info">
            <h2 className="header-title">Task Analytics</h2>
            <p className="header-subtitle">{task.subjectName} · {task.chapterName}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn-outline btn-outline--sm" onClick={onBack}>← Back</button>
          <button className="btn-primary" style={{ width: 'auto', padding: '10px 20px' }} onClick={() => onEdit?.(task)} disabled={deleting}>✏️ Edit Task</button>
          <button className="btn-deny" style={{ width: 'auto', padding: '10px 20px' }} onClick={handleDelete} disabled={deleting}>{deleting ? '…' : '🗑 Delete'}</button>
        </div>
      </header>

      <div className="section animate-in" style={{ padding: '32px' }}>
        <div className="td-info-card" style={{ 
          background: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(12px)',
          borderRadius: '24px',
          padding: '32px',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          marginBottom: '32px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '6px', background: 'var(--primary)' }}></div>
          <div className="td-badges" style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <span className="chip chip--sm">{task.subjectName}</span>
            <span className="chip chip--sm">📖 {task.chapterName}</span>
            {task.dueDate && (
              <span className={`chip chip--sm ${isDue ? 'chip--danger' : ''}`}>
                📅 Due {formatDate(task.dueDate)}
              </span>
            )}
          </div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '800', color: 'var(--text-primary)' }}>{task.title}</h1>
          {task.description && (
            <p style={{ marginTop: '16px', fontSize: '16px', lineHeight: '1.6', color: 'var(--text-secondary)', maxWidth: '800px' }}>{task.description}</p>
          )}

          {task.attachments?.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <p style={{ fontSize: '13px', fontWeight: '700', marginBottom: '12px', color: 'var(--text-primary)', opacity: 0.6 }}>ATTACHED FILES</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {task.attachments.map((att, i) => (
                  <a key={i} href={att.url} target="_blank" rel="noreferrer" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '8px 16px', 
                    background: '#fff', 
                    borderRadius: '50px', 
                    fontSize: '13px', 
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    textDecoration: 'none',
                    border: '1px solid rgba(0,0,0,0.05)',
                    transition: 'all 0.2s'
                  }}>
                    <span style={{ marginRight: '8px' }}>{fileIcon(att.name)}</span>
                    {att.name}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="stats-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '40px' }}>
          <div className="stat-card" style={{ padding: '24px', textAlign: 'center' }}>
            <span className="stat-num" style={{ fontSize: '32px', fontWeight: '800', color: 'var(--primary)' }}>{totalCount}</span>
            <span className="stat-label">Students Assigned</span>
          </div>
          <div className="stat-card" style={{ padding: '24px', textAlign: 'center', borderBottom: '4px solid var(--success)' }}>
            <span className="stat-num" style={{ fontSize: '32px', fontWeight: '800', color: 'var(--success)' }}>{submittedCount}</span>
            <span className="stat-label">Submissions</span>
          </div>
          <div className="stat-card" style={{ padding: '24px', textAlign: 'center', borderBottom: '4px solid #ff9f43' }}>
            <span className="stat-num" style={{ fontSize: '32px', fontWeight: '800', color: '#ff9f43' }}>{pendingCount}</span>
            <span className="stat-label">Pending</span>
          </div>
        </div>

        <div className="td-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800' }}>👥 Student Submissions</h3>
            <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{submittedCount} of {totalCount} completed</span>
          </div>

          {totalCount === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', background: 'rgba(255,255,255,0.3)', borderRadius: '20px', border: '2px dashed rgba(0,0,0,0.05)' }}>
              <p style={{ margin: 0, color: 'var(--text-secondary)' }}>No students have been assigned to this task yet.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Submitted students */}
              {task.assignedStudentIds
                ?.filter((sid) => submissionMap[sid])
                .map((sid) => {
                  const sub = submissionMap[sid];
                  const name = task.assignedStudentNames?.[sid] || sid;
                  return (
                    <div key={sid} className="student-task-card" style={{ padding: '16px 20px', cursor: 'default' }}>
                      <div className="stc-left">
                        <div className="student-avatar" style={{ background: 'var(--success-light)', color: 'var(--success)' }}>
                          {name.charAt(0).toUpperCase()}
                        </div>
                        <div className="stc-info">
                          <strong className="stc-name">{name}</strong>
                          <span className="stc-email">Submitted on {formatDate(sub.submittedAt)}</span>
                        </div>
                      </div>
                      <div className="stc-right">
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {sub.attachments?.map((att, i) => (
                            <a key={i} href={att.url} target="_blank" rel="noreferrer" style={{ 
                              padding: '6px 12px', 
                              background: '#fff', 
                              borderRadius: '8px', 
                              fontSize: '11px', 
                              fontWeight: '700',
                              color: 'var(--primary)',
                              border: '1px solid var(--primary-light)'
                            }}>
                              {fileIcon(att.name)} {att.name.length > 8 ? att.name.substring(0, 6) + '...' : att.name}
                            </a>
                          ))}
                        </div>
                        <span className="chip chip--success" style={{ marginLeft: '16px' }}>DONE</span>
                      </div>
                    </div>
                  );
                })}

              {/* Pending students */}
              {task.assignedStudentIds
                ?.filter((sid) => !submissionMap[sid])
                .map((sid) => {
                  const name = task.assignedStudentNames?.[sid] || sid;
                  return (
                    <div key={sid} className="student-task-card" style={{ padding: '16px 20px', cursor: 'default', opacity: 0.7 }}>
                      <div className="stc-left">
                        <div className="student-avatar" style={{ background: 'rgba(0,0,0,0.05)', color: 'var(--text-secondary)' }}>
                          {name.charAt(0).toUpperCase()}
                        </div>
                        <div className="stc-info">
                          <strong className="stc-name" style={{ color: 'var(--text-secondary)' }}>{name}</strong>
                          <span className="stc-email">Awaiting submission</span>
                        </div>
                      </div>
                      <div className="stc-right">
                        <span className="chip" style={{ background: 'rgba(0,0,0,0.05)', color: 'var(--text-secondary)' }}>PENDING</span>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
